import {
  Accordion,
  Alert,
  Button,
  Input,
  Spinner,
} from '@risksmart-app/atomic-ui';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AppetiteModel } from '@risksmart-app/domain/src/types/consts/appetite-model';
import { AppetiteModel as Appetite_Model_Enum } from '@risksmart-app/domain/src/types/consts/appetite-model';
import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RiskScoringModel } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import { RiskScoringModel as Risk_Scoring_Model_Enum } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import { getModuleConfigValue } from '@risksmart-app/modules/src/index';
import { type FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabHeader from 'src/components/tab-header/TabHeader';
import TabSettingsModal from 'src/components/tab-settings-modal/TabSettingsModal';
import { useModules } from 'src/context/moduleContext';
import {
  useInsertIngestionConfig,
  useUpdateIngestionConfig,
} from 'src/hooks/mutations/ingestion-config';
import { useGetIngestionConfigs } from 'src/hooks/queries/ingestion-config/useGetIngestionConfigs';
import { tryResolveOptimisticLockConflict } from 'src/hooks/useMutationResultNotification';
import { handleError } from 'src/utils/errorUtils';
import { z } from 'zod';

import ModuleSettings from './ModuleSettings';

type ModuleSettingsForm = {
  RiskScoringModel: RiskScoringModel;
  RiskScoringModelConfig: string;
  AppetiteCascadingModel: AppetiteModel;
  AppetiteCascadingModelConfig: string;
  IngestionConfig: string;
  IngestionApiKey: string;
};

const Tab: FC = () => {
  const {
    modules,
    toggle,
    commit,
    loading,
    isDirty,
    reset,
    setConfig,
    isModuleEnabled,
  } = useModules();
  const [showTabSettings, setShowTabSettings] = useState(false);
  const [tabSettingsParentType, setTabSettingsParentType] = useState<
    Parent_Type_Enum | undefined
  >();
  const [search, setSearch] = useState('');
  const [openModules, setOpenModules] = useState<string[]>([]);
  const { t } = useTranslation('common', { keyPrefix: 'modules' });
  const { t: ct } = useTranslation('common');
  const { addNotification } = useNotifications();

  const { data: ingestionConfigData } = useGetIngestionConfigs({
    queryArgs: {},
  });
  const existingIngestionConfig = ingestionConfigData?.ingestion_config?.[0];

  const { insertIngestionConfig } = useInsertIngestionConfig();
  const { updateIngestionConfig } = useUpdateIngestionConfig();

  const methods = useForm<ModuleSettingsForm>({
    defaultValues: {
      RiskScoringModel: Risk_Scoring_Model_Enum.Default,
      RiskScoringModelConfig: '{}',
      AppetiteCascadingModel: Appetite_Model_Enum.Default,
      AppetiteCascadingModelConfig: '{}',
      IngestionConfig: '{}',
      IngestionApiKey: '',
    },
    values: {
      RiskScoringModel: getModuleConfigValue(
        modules.risk?.subModules?.risk_scoring,
        {
          key: 'RiskScoringModel',
          schema: z.nativeEnum(Risk_Scoring_Model_Enum),
          defaultValue: Risk_Scoring_Model_Enum.Default,
        }
      ),
      RiskScoringModelConfig: getModuleConfigValue(
        modules.risk?.subModules?.risk_scoring,
        {
          key: 'RiskScoringModelConfig',
          schema: z.string().transform((v) => (v.trim() === '' ? '{}' : v)),
          defaultValue: '{}',
        }
      ),
      AppetiteCascadingModel: getModuleConfigValue(
        modules.risk?.subModules?.appetite_cascading,
        {
          key: 'AppetiteCascadingModel',
          schema: z.nativeEnum(Appetite_Model_Enum),
          defaultValue: Appetite_Model_Enum.Default,
        }
      ),
      AppetiteCascadingModelConfig: getModuleConfigValue(
        modules.risk?.subModules?.appetite_cascading,
        {
          key: 'AppetiteCascadingModelConfig',
          schema: z.string().transform((v) => (v.trim() === '' ? '{}' : v)),
          defaultValue: '{}',
        }
      ),
      IngestionConfig: existingIngestionConfig?.IngestionConfig
        ? JSON.stringify(existingIngestionConfig.IngestionConfig, null, 2)
        : '{}',
      IngestionApiKey: '',
    },
  });

  const saveChanges = async () => {
    const {
      RiskScoringModel,
      RiskScoringModelConfig,
      AppetiteCascadingModel,
      AppetiteCascadingModelConfig,
      IngestionConfig,
      IngestionApiKey,
    } = methods.getValues();
    setConfig('risk.subModules.risk_scoring', {
      RiskScoringModel,
      RiskScoringModelConfig,
    });
    setConfig('risk.subModules.appetite_cascading', {
      AppetiteCascadingModel,
      AppetiteCascadingModelConfig,
    });
    await commit({
      RiskScoringModel,
      RiskScoringModelConfig,
      AppetiteCascadingModel,
      AppetiteCascadingModelConfig,
    });

    const regFeedEnabled = isModuleEnabled('obligation.subModules.reg_feed');

    // The ingestion config is intentionally retained when the regulatory feed
    // module is disabled: disabling the module hides the customer-facing
    // Regulatory feed tab but preserves the saved selection so it is restored
    // on re-enable, avoiding silent data loss.
    if (regFeedEnabled) {
      const parsedConfig = JSON.parse(IngestionConfig);
      try {
        if (existingIngestionConfig) {
          await updateIngestionConfig({
            Id: existingIngestionConfig.Id,
            IngestionConfig: parsedConfig,
            OriginalTimestamp: existingIngestionConfig.ModifiedAtTimestamp,
            ApiKey: IngestionApiKey || undefined,
          });
        } else {
          await insertIngestionConfig({
            IngestionConfig: parsedConfig,
            ApiKey: IngestionApiKey || undefined,
          });
        }
      } catch (error) {
        const conflictMessage = tryResolveOptimisticLockConflict(error);
        if (conflictMessage) {
          handleError(error);
          addNotification({ type: 'error', content: conflictMessage });

          return;
        }

        // Preserve the existing behaviour for errors this settings surface
        // does not understand; only the recognised optimistic-lock conflict
        // is handled locally.
        throw error;
      }
    }
  };

  // Build a lowercased haystack per module from its translated title +
  // description and those of its sub-modules, so search matches what the user
  // actually sees (e.g. typing "chat" surfaces the AI module via its sub-module).
  const filteredModuleIds = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (moduleId: string, subModules?: Record<string, unknown>) =>
      [
        moduleId,
        // @ts-ignore — dynamic i18n keys
        t(`titles.${moduleId}`),
        // @ts-ignore — dynamic i18n keys
        t(`descriptions.${moduleId}`),
        ...Object.keys(subModules ?? {}).flatMap((subKey) => [
          subKey,
          // @ts-ignore — dynamic i18n keys
          t(`titles.${subKey}`),
          // @ts-ignore — dynamic i18n keys
          t(`descriptions.${subKey}`),
        ]),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query);

    return Object.entries(modules)
      .filter(([k, v]) => !query || matches(k, v.subModules))
      .map(([k]) => k);
  }, [modules, search, t]);

  // Auto-expand matches while searching so the matching content is visible.
  useEffect(() => {
    if (search.trim()) {
      setOpenModules(filteredModuleIds);
    }
  }, [search, filteredModuleIds]);

  // Mirrors `hasOptions` in ModuleSettings: only modules with config links or
  // sub-modules render an expandable body. Used to drive Open/Close all.
  const expandableModuleIds = useMemo(
    () =>
      filteredModuleIds.filter((id) => {
        const m = modules[id];
        const showConfigSection =
          m.enabled && (!!m.allowTabConfig || id === 'risk');

        return !!m.subModules || showConfigSection;
      }),
    [filteredModuleIds, modules]
  );

  const allExpanded =
    expandableModuleIds.length > 0 &&
    expandableModuleIds.every((id) => openModules.includes(id));

  // Toggling a module on auto-expands it when it has sub-modules, so the newly
  // available options are revealed immediately; toggling off collapses it.
  const handleModuleToggle = (id: string) => {
    const willEnable = !modules[id]?.enabled;
    toggle(id);
    if (willEnable && modules[id]?.subModules) {
      setOpenModules((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } else if (!willEnable) {
      setOpenModules((prev) => prev.filter((openId) => openId !== id));
    }
  };

  // Enabling a sub-module implies its parent must be on to take effect, so
  // auto-enable (and keep open) the top-level module if it isn't already.
  const handleSubModuleToggle = (fullId: string) => {
    toggle(fullId);
    const [parentId, subKey] = fullId.split('.subModules.');
    if (!subKey) {
      return;
    }
    const wasSubEnabled = !!modules[parentId]?.subModules?.[subKey]?.enabled;
    if (!wasSubEnabled && !modules[parentId]?.enabled) {
      toggle(parentId);
      setOpenModules((prev) =>
        prev.includes(parentId) ? prev : [...prev, parentId]
      );
    }
  };

  const settings = filteredModuleIds.map((k) => (
    <ModuleSettings
      key={k}
      moduleId={k}
      enabled={modules[k].enabled}
      toggleItem={handleSubModuleToggle}
      onModuleToggle={handleModuleToggle}
      setShowTabSettings={setShowTabSettings}
      setTabSettingsParentType={setTabSettingsParentType}
      subModules={modules[k].subModules}
      allowTabConfig={modules[k].allowTabConfig}
      isDirty={isDirty}
    />
  ));

  return (
    <div>
      <TabHeader className={'py-6'}>{t('tab_title')}</TabHeader>
      {isDirty && (
        <div className={'mb-4'}>
          <Alert variant={'active'} size={'sm'}>
            <Alert.Info />
            <Alert.Header>
              <Alert.Title>{t('unsaved_changes')}</Alert.Title>
            </Alert.Header>
          </Alert>
        </div>
      )}
      <div className={'mb-4 flex items-center justify-between gap-4'}>
        <div className={'w-full max-w-[420px]'}>
          <Input
            type={'search'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('search_placeholder')}
            aria-label={t('search_placeholder')}
          />
        </div>
        <Button
          variant={'neutral'}
          style={'ghost'}
          disabled={expandableModuleIds.length === 0}
          onClick={() => setOpenModules(allExpanded ? [] : expandableModuleIds)}
        >
          {allExpanded ? t('collapse_all') : t('expand_all')}
        </Button>
      </div>
      <FormProvider {...methods}>
        {settings.length > 0 ? (
          <Accordion
            multiple
            value={openModules}
            onValueChange={setOpenModules}
          >
            {settings}
          </Accordion>
        ) : (
          <p className={'py-4 text-grey500'}>{t('no_results')}</p>
        )}
      </FormProvider>
      <TabSettingsModal
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parentType={tabSettingsParentType}
        isOrgLevel={true}
      />
      <div
        className={
          'gap-2 border-0 border-grey150 border-solid border-t-[0.5px] pt-4'
        }
      >
        <div className={'flex gap-2'}>
          <Button onClick={saveChanges} disabled={loading}>
            {loading && <Spinner />}
            {ct('save')}
          </Button>
          <Button variant={'neutral'} style={'outline'} onClick={() => reset()}>
            {ct('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Tab;
